import {Component, computed, inject, Input} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {PermissionTypes} from "../../enum/PermissionTypes";
import {PermissionsService} from "../../services/permissions.service";
import {DatePipe} from "@angular/common";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {
  LotteryLicencesTableComponent
} from "../lottery-licences/lottery-licences-table/lottery-licences-table.component";
import {MatButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {PipesModule} from "../../pipes/pipes.module";
import {TestimonialsTableComponent} from "../testimonials/testimonials-table/testimonials-table.component";
import {LotteryLicenceAddComponent} from "../lottery-licences/lottery-licence-add/lottery-licence-add.component";
import {AddTestimonialComponent} from "../testimonials/add-testimonial/add-testimonial.component";
import {EditGameDescriptionComponent} from "../dialogs/edit-game-description/edit-game-description.component";
import {
  EditGameMoreInformationComponent
} from "../dialogs/edit-game-more-information/edit-game-more-information.component";
import {concatMap, of} from "rxjs";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {AppConfigService} from "../../services/app-config.service";
import {ActiveUserService} from "../../services/active-user.service";
import {MatDialog} from "@angular/material/dialog";
import {GameService} from "../../services/game.service";
import {SnackbarService} from "../../services/snackbar.service";
import {Router} from "@angular/router";
import {DataAccessReasonDialogComponent} from "../dialogs/data-access-reason/data-access-reason.component";
import {DataAccessService} from "../../services/data-access.service";
import {switchMap} from "rxjs/operators";
import {IDataAccessAuthorization} from "../../interfaces/audit/IDataAccessAuthorization";
import {
  ColComponent,
  ContainerComponent,
  ListGroupDirective,
  ListGroupItemDirective,
  RowComponent
} from "@coreui/angular";
import {GameSettingsScreenState} from "../../enum/GameSettingsScreenState";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {EditGameImageComponent} from "../dialogs/edit-game-image/edit-game-image.component";
import {EditQrCodeImageComponent} from "../dialogs/edit-qr-code-image/edit-qr-code-image.component";
import {
  EditNotificationSettingsComponent
} from "../dialogs/edit-notification-settings/edit-notification-settings.component";
import {
  EditCharityRegistrationUrlComponent
} from "../dialogs/edit-charity-registration-url/edit-charity-registration-url.component";
import {IUpdateGameSendLastFourDigitsOfWinningCard} from "../../interfaces/IUpdateGameSendLastFourDigitsOfWinningCard";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {GameTypeEnum} from "../../enum/GameTypeEnum";
import {GameStateEnum} from "../../enum/GameStateEnum";
import {GameStateSettingsComponent} from "../game-state-settings/game-state-settings.component";
import {
  EditNotificationDeliverySettingsComponent
} from "../dialogs/edit-notification-delivery-settings/edit-notification-delivery-settings.component";
import {UtilService} from "../../services/util.service";

@Component({
  selector: 'app-game-settings',
  standalone: true,
  imports: [
    DatePipe,
    HasPermissionDirective,
    LotteryLicencesTableComponent,
    MatButton,
    MatIcon,
    MatTooltip,
    PipesModule,
    TestimonialsTableComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    ListGroupDirective,
    ListGroupItemDirective,
    MatCard,
    MatCardHeader,
    MatCardContent,
    MatCardTitle,
    MatProgressSpinner,
    GameStateSettingsComponent
  ],
  templateUrl: './game-settings.component.html',
  styleUrl: './game-settings.component.scss'
})
export class GameSettingsComponent {
  @Input() activeGame: IGameQueryResult | null = null;

  private appConfigService: AppConfigService = inject(AppConfigService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private gameService: GameService = inject(GameService);
  private router: Router = inject(Router);
  private matDialog: MatDialog = inject(MatDialog);
  private utilService: UtilService = inject(UtilService);
  private dataAccessService: DataAccessService = inject(DataAccessService);
  private permissionsService: PermissionsService = inject(PermissionsService);

  public cdnRoot = this.appConfigService.cdnRoot();
  public activeUser = this.activeUserService.activeUser();

  public screenState = GameSettingsScreenState.GeneralSettings;
  public screenStateEnum = GameSettingsScreenState;
  public uiState = UIStateEnum.ShowData;
  public uiStateEnum = UIStateEnum;

  protected readonly permissionTypes = PermissionTypes;
  protected readonly charityRegistrationUrlHint =
    'Optional link to the form charities and nonprofits use to ask to join the fundraiser. It appears as a "Register your association" button on the landing page; leave empty to hide it.';

  private static readonly GAME_STATE_PERMISSIONS = [
    PermissionTypes.ForceDrawWinner,
    PermissionTypes.PauseGameInstance,
    PermissionTypes.StopGame,
    PermissionTypes.EndGame
  ];

  private activeGame$ = toSignal(this.gameService.selectActiveGame$(), {initialValue: undefined});

  // The tab is nothing but the four actions it contains; the state itself reads on General Settings.
  // Computed because userHasPermission resolves per-game, so the answer changes with the selected game.
  protected readonly canViewGameStateSettings = computed(() =>
    this.activeGame$() !== undefined
    && GameSettingsComponent.GAME_STATE_PERMISSIONS.some(
      (permissionTypeP) => this.permissionsService.userHasPermission(permissionTypeP)));

  protected readonly GameTypeEnum = GameTypeEnum;
  protected readonly GameStatuses = GameStateEnum;

  private static readonly PAUSE_STATES = [
    GameStateEnum.Paused,
    GameStateEnum.NoLicense,
    GameStateEnum.PauseAfterNextDraw,
    GameStateEnum.PauseAfterNextWinner,
    GameStateEnum.PauseAfterNextDrawCancelAutoplays,
    GameStateEnum.PauseAfterNextWinnerCancelAutoplays
  ];

  protected isPausedOrPendingPause(): boolean {
    return this.activeGame !== null && GameSettingsComponent.PAUSE_STATES.includes(this.activeGame.State);
  }

  async editGameRules() {
    await this.router.navigateByUrl(`${this.activeGame?.Id}/edit-game-rules`);
  }

  async editHowToPlay() {
    await this.router.navigateByUrl(`${this.activeGame?.Id}/edit-how-to-play`)
  }

  openAddLicenceNumber() {
    this.matDialog.open(LotteryLicenceAddComponent, {
      data: this.activeGame,
      width: '650px'
    });
  }

  openAddTestimonial() {
    this.matDialog.open(AddTestimonialComponent, {
      data: this.activeGame,
      width: '650px'
    });
  }

  openEditGameDescriptionDialog() {
    this.matDialog.open(EditGameDescriptionComponent, {
      data: this.activeGame,
      width: '650px',
    }).afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame?.Id);
        }
        return of(null);
      })).subscribe({
      next: (res: IGameQueryResult | null) => {
        if (res) {
          this.activeGame = res;
        }
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.')
      }
    })
  }

  openEditGameMoreInformationDialog() {
    this.matDialog.open(EditGameMoreInformationComponent, {
      data: this.activeGame,
      width: '900px',
      maxWidth: '95vw',
    }).afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame?.Id);
        }
        return of(null);
      })).subscribe({
      next: (res: IGameQueryResult | null) => {
        if (res) {
          this.activeGame = res;
        }
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.')
      }
    })
  }

  openGameLogoImageDialog() {
    this.matDialog.open(EditGameImageComponent, {
      data: this.activeGame,
      width: '650px'
    }).afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame.Id);
        }
        return of(null);
      })).subscribe({
      next: (res: IGameQueryResult | null) => {
        if (res) {
          this.activeGame = res;
        }
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.')
      }
    });
  }

  openGameQrCodeImageDialog() {
    this.matDialog.open(EditQrCodeImageComponent, {
      data: this.activeGame,
      width: '650px',
    });
  }

  openEditCharityRegistrationUrlDialog() {
    this.matDialog.open(EditCharityRegistrationUrlComponent, {
      data: this.activeGame,
      width: '650px',
    }).afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame?.Id);
        }
        return of(null);
      })).subscribe({
      next: (res: IGameQueryResult | null) => {
        if (res) {
          this.activeGame = res;
        }
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.')
      }
    })
  }

  openEditNotificationDeliverySettingsDialog() {
    this.matDialog.open(EditNotificationDeliverySettingsComponent, {
      data: this.activeGame,
      width: '650px',
    }).afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame?.Id);
        }
        return of(null);
      })).subscribe({
      next: (res: IGameQueryResult | null) => {
        if (res) {
          this.activeGame = res;
        }
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.')
      }
    })
  }

  openEditNotificationSettingsDialog() {
    this.matDialog.open(EditNotificationSettingsComponent, {
      data: this.activeGame,
      width: '650px',
    }).afterClosed()
      .pipe(concatMap((res) => {
        if (res && this.activeGame) {
          return this.gameService.getGameByGameId(this.activeGame?.Id);
        }
        return of(null);
      })).subscribe({
      next: (res: IGameQueryResult | null) => {
        if (res) {
          this.activeGame = res;
        }
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Looks like there was an error retrieving your game info. Please try again or contact a system admin.')
      }
    })
  }

  updateGameSendLastFourDigitsStatus() {
    this.uiState = UIStateEnum.ShowLoading;

    let command : IUpdateGameSendLastFourDigitsOfWinningCard = {
      AdminId: this.activeUser.Id,
      GameId: this.activeGame?.Id!
    };

    this.gameService.updateGameSendLastFourDigitsOfWinningCard(command).subscribe({
      next: () => {
        this.activeGame!.SendLastFourDigitsOfWinningCreditCardToOrganization = !this.activeGame!.SendLastFourDigitsOfWinningCreditCardToOrganization;
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openSuccessfulSnackBar(`Successfully updated game ${this.activeGame!.SendLastFourDigitsOfWinningCreditCardToOrganization ? ' ' : 'not '}to send last four digits of winning card to organization`);
      },
      error: err => {
        this.uiState = UIStateEnum.ShowData;
        this.snackbarService.openErrorSnackBar("Failed to update the send last four digits status");
      }
    })
  }

  downloadPlayerEmails() {
    if (!this.activeGame) {
      return;
    }

    if (this.appConfigService.privacyPromptsDisabled()) {
      this.downloadPlayerEmailsDirectly();
    } else {
      this.downloadPlayerEmailsWithDialog();
    }
  }

  private downloadPlayerEmailsDirectly() {
    if (!this.activeGame) {
      return;
    }

    this.gameService.getActivePlayerEmails(this.activeGame.Id).subscribe({
      next: (emails) => {
        if (emails.length === 0) {
          this.snackbarService.openErrorSnackBar('No active players found for this game');
          return;
        }

        const csv_content = emails.map(e => e.Email).join('\n');
        const filename = `player-emails-${this.activeGame!.Name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
        this.utilService.triggerDownload(csv_content, filename);
        this.snackbarService.openSuccessfulSnackBar(`Downloaded ${emails.length} player emails`);
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Failed to download player emails');
      }
    });
  }

  private downloadPlayerEmailsWithDialog() {
    if (!this.activeGame) {
      return;
    }

    const dialogRef = this.matDialog.open(DataAccessReasonDialogComponent, {
      data: { gameId: this.activeGame.Id },
      disableClose: true,
      width: '900px'
    });

    dialogRef.afterClosed().pipe(
      switchMap(result => {
        if (result && this.activeGame) {
          // Log the data access (no PlayerId for bulk export)
          const auditData: IDataAccessAuthorization = {
            GameId: this.activeGame.Id,
            AccessReason: result.accessReason,
            AdditionalDetails: result.additionalDetails || '',
            PlayerName: `Bulk Email Export - ${this.activeGame.Name}`
          };

          return this.dataAccessService.logDataAccessAuthorization(auditData).pipe(
            switchMap(() => this.gameService.getActivePlayerEmails(this.activeGame!.Id))
          );
        }
        return of(null);
      })
    ).subscribe({
      next: (emails) => {
        if (emails && emails.length > 0) {
          const csv_content = emails.map(e => e.Email).join('\n');
          const filename = `player-emails-${this.activeGame!.Name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
          this.utilService.triggerDownload(csv_content, filename);
          this.snackbarService.openSuccessfulSnackBar(`Downloaded ${emails.length} player emails`);
        } else if (emails && emails.length === 0) {
          this.snackbarService.openErrorSnackBar('No active players found for this game');
        }
      },
      error: () => {
        this.snackbarService.openErrorSnackBar('Failed to download player emails');
      }
    });
  }
}
